import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { ComposeButton } from '@/features/composer/ComposeButton';
import { NavLink, useLocation } from 'react-router-dom';

import type { PublicUser } from '@/lib/schemas/user';
import { layout } from '@/theme';

import { visibleNavItems } from './navigation';

interface NavRailProps {
  isSignedIn: boolean;
  role?: PublicUser['role'];
  /** The business this account speaks for, and what its plan allows. */
  businessSlug?: string | null;
  capabilities?: string[];
  onCompose: () => void;
}

/** Desktop left column (spec §9.3). Hidden below `md`, where the bottom bar takes over. */
export function NavRail({ isSignedIn, role, businessSlug, capabilities, onCompose }: NavRailProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const items = visibleNavItems({ isSignedIn, role, businessSlug, capabilities });

  return (
    <Box
      component="nav"
      aria-label={t('nav.main')}
      sx={{
        width: layout.navRailWidth,
        flexShrink: 0,
        display: { xs: 'none', md: 'block' },
        position: 'sticky',
        top: layout.appBarHeight + 16,
        alignSelf: 'flex-start',
        maxHeight: `calc(100vh - ${layout.appBarHeight + 32}px)`,
        overflowY: 'auto',
      }}
    >
      <Stack spacing={2}>
        <List disablePadding>
          {items.map((item) => {
            const selected = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);

            return (
              <ListItem key={item.to} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={NavLink}
                  to={item.to}
                  selected={selected}
                  aria-current={selected ? 'page' : undefined}
                  sx={{ py: 1, px: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>
                    <item.Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.labelKey)}
                    primaryTypographyProps={{
                      fontWeight: selected ? 650 : 500,
                      fontSize: '0.9375rem',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {isSignedIn && <ComposeButton onCompose={onCompose} fullWidth />}

        {/* The product's own three lines, sitting quietly at the bottom of the
            rail. The last one is the point, so it carries the gold. */}
        <Stack spacing={0.25} sx={{ px: 1.5, pt: 1 }} aria-hidden>
          {/* `text.secondary`, not `text.disabled`: the disabled tone is 2.7:1
              on the page background, which fails AA at 13px. */}
          <Typography variant="caption" color="text.secondary">
            {t('tagline.good')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('tagline.bad')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'ideal.text' }}>
            {t('tagline.ideal')}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
